'use client'

import { useMemo } from 'react'
import type { Team } from '@skop-app/lib-domain/types'

import { useUserContext } from '@/context'
import {
  useOrganizationMetadata,
  useOrganizationTree,
} from '@/services/queries/team'
import { useSelectedTeamMembers } from '@/hooks/useSelectedTeamMembers'

type UseSelectedTeamDetailsOptions = {
  includeMembers?: boolean
  includeMetadata?: boolean
  metadataKeys?: readonly string[]
  tree?: boolean
}

export function useSelectedTeamDetails(
  options?: UseSelectedTeamDetailsOptions,
) {
  const {
    includeMembers = false,
    includeMetadata = false,
    metadataKeys,
    tree = false,
  } = options ?? {}
  const { organizationScope } = useUserContext()
  const selectedOrganization = organizationScope.selectedOrganization
  const selectedOrganizationId = organizationScope.selectedOrganizationId

  const { data: treeData, isLoading: isLoadingTree } = useOrganizationTree(
    tree ? selectedOrganizationId : null,
  )
  const { data: members = [], isLoading: isLoadingMembers } =
    useSelectedTeamMembers(includeMembers ? selectedOrganizationId : undefined)

  const shouldLoadMetadata = includeMetadata || Boolean(metadataKeys?.length)
  const { data: metadata = [], isLoading: isLoadingMetadata } =
    useOrganizationMetadata(
      shouldLoadMetadata ? selectedOrganizationId : null,
      metadataKeys,
    )

  const data = useMemo<Team | undefined>(() => {
    const base = (tree ? treeData : selectedOrganization) ?? undefined
    if (!base) return undefined

    return {
      ...base,
      members: includeMembers ? members : base.members,
      metadata: shouldLoadMetadata ? metadata : base.metadata,
    }
  }, [
    includeMembers,
    members,
    metadata,
    shouldLoadMetadata,
    selectedOrganization,
    tree,
    treeData,
  ])

  return {
    data,
    isLoading:
      (tree && isLoadingTree) ||
      (includeMembers && isLoadingMembers) ||
      (shouldLoadMetadata && isLoadingMetadata),
  }
}
